import Image from "next/image";
import { useRouter } from "next/router";
import React, { useMemo } from "react";
import { USER_MENU_TYPE } from "src/constants/userProfile";
import { color } from "src/styles/globals";
import { Body } from "sunday-component";
import { maskProfilePhoneNumber } from "src/utils/maskData";
import { User } from "src/types/user";
import { useTranslation } from "react-i18next";
import { filterParams } from "src/utils/url";
import { GLOBAL_PARAMS } from "src/constants/params";
import { logout } from "src/api/auth";
import { logError } from "src/utils/logger";
import { formatMobileNumberToInternal } from "src/utils/format";
import { I18Text } from "src/types/i18Text";
import BulletListIcon from "../Common/Icons/BulletListIcon";
import { SettingIcon } from "../Common/Icons/SettingIcon";
import {
  CustomMenu,
  LogoutTitle,
  MainMenuItem,
  ProfileMenuItem,
  ProfileName,
  Title,
} from "./style/UserMenu.style";
import AffiliateIcon from "../Common/Icons/AffiliateIcon";

export type MenuConfig = {
  key: string;
  type: string;
  title: I18Text;
  onClick: () => void;
  icon: JSX.Element | string;
  hide?: boolean;
};

const renderIcon = (menu: MenuConfig, isSelected: boolean) => {
  if (typeof menu.icon === "string") return menu.icon;
  return React.cloneElement(menu.icon, {
    color: isSelected ? color.carrotOrange : color.charcoal10,
  });
};

type UserProfileMenuProps = {
  user: User;
};

const UserProfileMenu = (props: UserProfileMenuProps) => {
  const { t } = useTranslation("user-profile");
  const { user } = props;
  const { locale, asPath, push, query, reload } = useRouter();
  const selectedMenu = asPath.split("/").pop().split("?")[0];
  const authUrl = process.env.NEXT_PUBLIC_AUTH_URL;

  const menus: MenuConfig[] = useMemo(
    () => [
      {
        key: "orders",
        type: USER_MENU_TYPE.MAIN,
        title: {
          th: "ดูสถานะคำสั่งซื้อ",
          en: "View order status",
        },
        onClick: () => {
          push({
            pathname: `/user/orders`,
            query: filterParams(query, GLOBAL_PARAMS),
          });
        },
        icon: <BulletListIcon />,
      },
      {
        key: "affiliate",
        type: USER_MENU_TYPE.MAIN,
        title: {
          th: "Sunday Affiliate Program",
          en: "Sunday Affiliate Program",
        },
        onClick: () => {
          push({
            pathname: `/affiliate`,
            query: filterParams(query, GLOBAL_PARAMS),
          });
        },
        icon: <AffiliateIcon />,
      },
      {
        key: "setting",
        type: USER_MENU_TYPE.MAIN,
        title: {
          th: "ตั้งค่าบัญชีผู้ใช้",
          en: "Account settings",
        },
        onClick: () => {
          window.open(`${authUrl}/${locale}/setting/profile-info`, "_blank");
        },
        icon: <SettingIcon />,
        hide: true,
      },
      {
        key: "logout",
        type: USER_MENU_TYPE.LOGOUT,
        title: {
          th: "ออกจากระบบ",
          en: "Logout",
        },
        onClick: () => {},
        icon: "/static/pages/userProfile/log-out.svg",
        hide: true,
      },
    ],
    [authUrl, locale, push, query]
  );

  const mainSection = menus.filter((menu) => menu.type === USER_MENU_TYPE.MAIN);
  const logoutMenu = menus.find((menu) => menu.type === USER_MENU_TYPE.LOGOUT);
  const mobileNo = formatMobileNumberToInternal(
    user?.profile.contacts.primaryphone
  );

  const handleLogOut = async () => {
    try {
      await logout();
    } catch (error) {
      logError("[profile-header] fail on logout", error);
    }

    reload();
  };

  return (
    <CustomMenu mode="inline" defaultSelectedKeys={["1"]} selectable={false}>
      <ProfileMenuItem
        disabled
        columnGap="16px"
        icon={
          <Image
            src="/static/profile-menu-default-user-icon.svg"
            height={32}
            width={32}
            alt=""
          />
        }
        key="user-profile-menu-user"
      >
        {user?.profile.name ? (
          <ProfileName>{user?.profile.name}</ProfileName>
        ) : (
          <ProfileName>{t("defaultProfileName")}</ProfileName>
        )}
        <Body level={7}>{maskProfilePhoneNumber(mobileNo)}</Body>
      </ProfileMenuItem>
      {mainSection.map((menu) => {
        const isSelected = menu.key === selectedMenu;
        if (menu.hide) return null;
        return (
          <MainMenuItem
            selected={isSelected}
            icon={renderIcon(menu, isSelected)}
            key={`user-profile-menu-${menu.key}`}
            onClick={() => menu.onClick()}
          >
            <Title level={7}>{menu.title[locale]}</Title>
          </MainMenuItem>
        );
      })}
      <MainMenuItem
        selected={false}
        icon={
          <Image
            width={24}
            height={24}
            src={logoutMenu.icon as string}
            alt=""
          />
        }
        key="user-profile-logout"
        onClick={handleLogOut}
        hidden={logoutMenu.hide}
      >
        <LogoutTitle level={7}>{logoutMenu.title[locale]}</LogoutTitle>
      </MainMenuItem>
    </CustomMenu>
  );
};

export default UserProfileMenu;
